<template>
	<div class="content">
		<div class="actions-container">
			<PermissionsWrapper
				:permissionIds="[Permissions.DocumentProcessing]"
				:permissionLevel="PermissionLevels.Create"
			>
				<button @click="isUploadModalOpen = true">
					<DocumentArrowUpIcon class="action-icon" />
					Upload Document
				</button>
			</PermissionsWrapper>
			<button @click="getDocumentsAsync" class="secondary">
				<ArrowPathIcon class="action-icon" />
				Refresh
			</button>
		</div>
		<div v-if="isLoading" class="spinner-container">
			<Spinner />
		</div>
		<div v-else class="table-wrapper">
			<table>
				<thead>
					<tr>
						<th
							v-for="header in headers"
							:key="header.key"
							:class="[header.class, isActiveSort(header.key)]"
							@click="sortBy(header.key)"
						>
							<span class="header-label">
								{{ header.label }}
								<span v-if="header.sortable">
									{{ getSortArrow(header.key) }}
								</span>
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					<DocumentRow
						v-for="document in sortedDocuments"
						:key="document.id"
						:document="document"
						:borrowers="borrowers"
						@documentChangedAsync="getDocumentsAsync"
					/>
				</tbody>
			</table>
		</div>

		<UploadDocumentModal
			v-if="isUploadModalOpen"
			:documentEntityId="documentEntityId"
			:loanId="loanId"
			:borrowers="borrowers"
			@close="closeUploadModal"
		/>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, ref, computed } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import DocumentRow from '@/components/loan/documents/DocumentRow.vue';
	import { DocumentArrowUpIcon, ArrowPathIcon } from '@heroicons/vue/24/solid';
	import UploadDocumentModal from '@/components/loan/documents/UploadDocumentModal.vue';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import { Borrower, LoanDocument } from '@/models/loans';

	const props = defineProps<{
		documentEntityId: number;
		loanId: number;
		borrowers: Borrower[];
		referenceId: string;
	}>();

	const route = useRoute();
	const router = useRouter();

	const isLoading = ref<boolean>(false);
	const documents = ref<LoanDocument[]>([]);
	const isUploadModalOpen = ref(false);

	const headers = ref([
		{ label: 'Name', sortable: true, key: 'name' },
		{ label: 'Upload Date & Time', sortable: true, key: 'uploadAtUtcFormatted' },
		{ label: 'Document Type', sortable: true, key: 'documentTypeHumanized' },
		{ label: 'Borrower', sortable: true, key: 'borrowerId' },
		{ label: '', sortable: false, key: '', class: 'no-hover' }
	]);

	const closeUploadModal = () => (isUploadModalOpen.value = false);

	const sortKey = ref('');
	const sortOrder = ref(1);

	const sortedDocuments = computed(() => {
		if (!sortKey.value) {
			return documents.value; // no sorting, return original order
		}

		return [...documents.value].sort((a, b) => {
			let valA = a ? a[sortKey.value] : null;
			let valB = b ? b[sortKey.value] : null;

			if (typeof valA === 'string') {
				valA = valA.toLowerCase();
			}

			if (typeof valB === 'string') {
				valB = valB.toLowerCase();
			}

			if (valA < valB) {
				return -1 * sortOrder.value;
			} else if (valA > valB) {
				return 1 * sortOrder.value;
			}

			return 0;
		});
	});

	const sortBy = (key: string) => {
		if (sortKey.value === key) {
			if (sortOrder.value === 1) {
				sortOrder.value = -1; // second click, descending
			} else if (sortOrder.value === -1) {
				sortKey.value = ''; // third click, reset sort
				sortOrder.value = 1; // default sort order for the next click
			}
		} else {
			sortKey.value = key;
			sortOrder.value = 1; // first click, ascending
		}

		router.push({
			query: {
				...route.query,
				documentsSortKey: sortKey.value,
				documentsSortOrder: sortOrder.value.toString()
			},
			hash: route.hash
		});
	};

	const getSortArrow = (key: string) => {
		if (sortKey.value === key) {
			return sortOrder.value === 1 ? '▲' : sortOrder.value === -1 ? '▼' : ' ';
		}

		return '';
	};

	const isActiveSort = (key: string) => (sortKey.value === key ? 'active-sort' : '');

	async function getDocumentsAsync() {
		isLoading.value = true;

		try {
			const response = await axios(`/api/loandocuments/loan/${props.referenceId}`);

			documents.value = response.data;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue getting the loan documents. Please try again later.'
			});
		} finally {
			isLoading.value = false;
		}
	}

	function intializeSorting() {
		const querySortKey = route.query.documentsSortKey as string;
		const querySortOrder = parseInt(route.query.documentsSortOrder as string, 10);

		if (querySortKey) {
			sortKey.value = querySortKey;
		}

		if (!isNaN(querySortOrder) && (querySortOrder === 1 || querySortOrder === -1)) {
			sortOrder.value = querySortOrder;
		}
	}

	onMounted(async () => {
		intializeSorting();

		await getDocumentsAsync();
	});
</script>

<style scoped>
	.spinner-container {
		display: flex;
		justify-content: center;
		margin-top: 4rem;
	}

	.content {
		display: flex;
		gap: 1rem;
	}

	.table-wrapper {
		overflow-x: auto;
		width: 100%;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 14px;
		border: 1px solid #ccc;
	}

	thead th {
		background-color: #f2f2f2;
		font-weight: 800;
		padding: 10px 20px;
		border-bottom: 1px solid #ccc;
		cursor: pointer;
		user-select: none;
		text-align: left;
	}

	.header-label {
		white-space: nowrap;
		line-height: 1;
	}

	th:hover {
		background-color: #ddd;
	}

	.no-hover:hover {
		background-color: #f2f2f2;
		cursor: default;
	}

	tr:nth-child(even):hover {
		background-color: #f2f2f2;
	}

	td:nth-child(1) {
		width: 40px;
	}

	.actions-container {
		display: flex;
		gap: 1rem;
		justify-content: end;
	}

	.action-icon {
		width: 1rem;
		height: 1rem;
		margin: 3px;
	}

	.actions-container button {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 8px 16px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-family: 'Montserrat', Arial, sans-serif;
		transition: background-color 0.2s, color 0.2s;
		font-size: 14px;
		gap: 8px;
		min-width: 98px;
	}

	.actions-container button:hover {
		background-color: #333;
	}

	.actions-container button.secondary {
		color: black;
		background-color: white;
		box-shadow: 0 0 0 1px #ccc;
	}

	.actions-container button.secondary:hover {
		background-color: black;
		color: white;
		border: none;
		box-shadow: 0 0 0 1px black;
	}
</style>
